import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from './LoadingSpinner';
import ErrorBoundary from './ErrorBoundary';

const RevisionHistory = ({ parrotId }) => {
  const { theme } = useTheme();
  const { showSuccess, showError } = useToast();
  const { execute, loading, error } = useApi();
  
  // State management
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [revertingRevision, setRevertingRevision] = useState(null);
  const [showRevertConfirm, setShowRevertConfirm] = useState(null);

  const fetchRevisions = useCallback(async () => {
    try {
      await execute(async (api) => {
        const revisionsData = await api.get(`/api/parrots/${parrotId}/revisions`);
        setRevisions(revisionsData);
        return revisionsData;
      }, { showErrorToast: true });
    } catch (error) {
      console.error('Failed to fetch revisions:', error);
    }
  }, [execute, parrotId]);

  // Load revisions on component mount
  useEffect(() => {
    if (parrotId) {
      fetchRevisions();
    }
  }, [parrotId, fetchRevisions]);

  const handleRevert = async (revisionId) => {
    try {
      setRevertingRevision(revisionId);
      
      await execute(async (api) => {
        await api.post(`/api/parrots/${parrotId}/revert/${revisionId}`);
        // Refresh revisions to show the new revert revision
        await fetchRevisions();
        setShowRevertConfirm(null);
        showSuccess('Successfully reverted to previous version');
      });
    } catch (error) {
      console.error('Failed to revert revision:', error);
    } finally {
      setRevertingRevision(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getConfigurationDiff = (currentConfig, previousConfig) => {
    const diffs = [];
    
    // Helper function to compare nested objects
    const compareObjects = (current, previous, path = '') => {
      const allKeys = new Set([...Object.keys(current || {}), ...Object.keys(previous || {})]);
      
      for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;
        const currentValue = current?.[key];
        const previousValue = previous?.[key];
        
        if (typeof currentValue === 'object' && typeof previousValue === 'object' && 
            currentValue !== null && previousValue !== null) {
          compareObjects(currentValue, previousValue, currentPath);
        } else if (currentValue !== previousValue) {
          diffs.push({
            field: currentPath,
            current: currentValue,
            previous: previousValue
          });
        }
      }
    };
    
    compareObjects(currentConfig, previousConfig);
    return diffs;
  };

  const renderDiffValue = (value) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-500 italic">null</span>;
    }
    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-400' : 'text-red-400'}>{value.toString()}</span>;
    }
    if (typeof value === 'string') {
      return <span className="text-blue-400">"{value}"</span>;
    }
    return <span className="text-yellow-400">{JSON.stringify(value)}</span>;
  };

  if (loading) {
    return (
      <div className={`rounded-lg p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
      }`}>
        <h3 className={`text-xl font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Revision History</h3>
        <LoadingSpinner text="Loading revision history..." className="py-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
      }`}>
        <h3 className={`text-xl font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Revision History</h3>
        <div className={`border px-4 py-3 rounded-lg ${
          theme === 'dark' 
            ? 'bg-red-900 border-red-700 text-red-200' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary fallbackMessage="There was an error loading the revision history. Please refresh to try again.">
      <div className={`rounded-lg p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
      }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-xl font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Revision History</h3>
        <span className={`text-sm ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {revisions.length} revision{revisions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {revisions.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-400">No revision history available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {revisions.map((revision, index) => {
            const isLatest = revision.is_current;
            const previousRevision = index < revisions.length - 1 ? revisions[index + 1] : null;
            
            return (
              <div
                key={revision.id}
                className={`border rounded-lg p-4 transition-all ${
                  isLatest 
                    ? 'border-teal-500 bg-teal-900/20' 
                    : theme === 'dark'
                      ? 'border-gray-600 bg-gray-700/50'
                      : 'border-gray-300 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        isLatest 
                          ? 'bg-teal-600 text-white' 
                          : theme === 'dark'
                            ? 'bg-gray-600 text-gray-200'
                            : 'bg-gray-200 text-gray-700'
                      }`}>
                        Version {revision.version_number}
                      </span>
                      {isLatest && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-600 text-white dark:text-white">
                          Current
                        </span>
                      )}
                      <span className="text-sm text-gray-400">
                        {formatDate(revision.created_at)}
                      </span>
                    </div>
                    
                    {revision.change_summary && (
                      <p className="text-sm text-gray-300 mb-3 dark:text-gray-600">
                        {revision.change_summary}
                      </p>
                    )}

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedRevision(selectedRevision === revision.id ? null : revision.id);
                          setShowDiff(false);
                        }}
                        className="text-xs text-teal-400 hover:text-teal-300 underline"
                      >
                        {selectedRevision === revision.id ? 'Hide Details' : 'View Details'}
                      </button>
                      
                      {previousRevision && (
                        <button
                          onClick={() => {
                            setSelectedRevision(revision.id);
                            setShowDiff(!showDiff);
                          }}
                          className="text-xs text-blue-400 hover:text-blue-300 underline"
                        >
                          Compare with Previous
                        </button>
                      )}
                      
                      {!isLatest && (
                        <button
                          onClick={() => setShowRevertConfirm(revision.id)}
                          disabled={revertingRevision === revision.id}
                          className="text-xs text-yellow-400 hover:text-yellow-300 underline disabled:opacity-50"
                        >
                          {revertingRevision === revision.id ? 'Reverting...' : 'Revert to This Version'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Revision Details */}
                {selectedRevision === revision.id && (
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    {showDiff && previousRevision ? (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-3 dark:text-white">
                          Changes from Version {previousRevision.version_number}
                        </h4>
                        <div className="space-y-2">
                          {(() => {
                            const diffs = getConfigurationDiff(revision.configuration, previousRevision.configuration);
                            if (diffs.length === 0) {
                              return (
                                <p className="text-sm text-gray-400 italic">No configuration changes detected</p>
                              );
                            }
                            return diffs.map((diff, diffIndex) => (
                              <div key={diffIndex} className="bg-gray-900 rounded p-3 text-sm">
                                <div className="font-medium text-gray-300 mb-1 dark:text-gray-600">{diff.field}</div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="text-red-400 text-xs">Previous:</span>
                                    <div className="mt-1">{renderDiffValue(diff.previous)}</div>
                                  </div>
                                  <div>
                                    <span className="text-green-400 text-xs">Current:</span>
                                    <div className="mt-1">{renderDiffValue(diff.current)}</div>
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-3 dark:text-white">Configuration</h4>
                        <div className="bg-gray-900 rounded p-3 text-xs">
                          <pre className="text-gray-300 whitespace-pre-wrap overflow-x-auto dark:text-gray-600">
                            {JSON.stringify(revision.configuration, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Revert Confirmation Dialog */}
      {showRevertConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4 dark:text-white">Confirm Revert</h3>
            <p className="text-gray-300 mb-6 dark:text-gray-600">
              Are you sure you want to revert to version {revisions.find(r => r.id === showRevertConfirm)?.version_number}? 
              This will create a new revision with the previous configuration.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRevertConfirm(null)}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded hover:bg-gray-700 dark:text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevert(showRevertConfirm)}
                disabled={revertingRevision}
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 dark:text-white"
              >
                {revertingRevision ? 'Reverting...' : 'Revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default RevisionHistory;
